import type { GameState, CardDef } from '../types.js';
import type { Target } from '../shared/targeting.js';
import { isPositiveVerb } from '../shared/targeting.js';
import { getPlayerState, getOpponentSide, getInstance, getEffectivePower } from './GameState.js';

// Resolve a Target into the list of character instance IDs the verb should
// operate on. Callers pass the source character (for self-referential targets
// and the weakest/strongest-ally fallback behavior).
//
// Invariants preserved from the legacy slugs:
//   - weakest-ally / strongest-ally: exclude source while searching, but fall
//     back to source itself if no other allies exist. This matches the old
//     boost-weakest-ally / armor-strongest-ally behavior.
//   - all-allies includes source; all-other-allies excludes source. This
//     distinction is what separates the old armor-all (include) from the old
//     boost-allies (exclude).
//   - all selectors skip empty ring slots and return only live character ids.
export function selectTargets(
  state: GameState,
  sourceId: string,
  catalog: Map<string, CardDef>,
  target: Target,
  /** When set, biases tie-breaks for `strongest-character` /
   *  `weakest-character` toward whichever side benefits the active
   *  player given the verb's sign, AND filters the candidate pool
   *  to skip targets the verb would no-op against (currently:
   *  `exhaust` skips already-exhausted characters so e.g.
   *  strongest-enemy picks the strongest *non-exhausted* enemy
   *  instead of wasting on a tapped one). Positive verbs (boost /
   *  armor / ready) prefer the active player's own side; negative
   *  verbs (damage / slay / destroy / return / exhaust) prefer the
   *  opponent's. Other targets ignore the tie-bias. Verb-less calls
   *  apply no eligibility filtering and fall back to active-player-
   *  side preference. */
  verb?: string,
): string[] {
  const src = getInstance(state, sourceId);
  const myRing = getPlayerState(state, src.owner).ring;
  const oppRing = getPlayerState(state, getOpponentSide(src.owner)).ring;

  // Eligibility predicate. The user's framing: "any targeting rule
  // that tries to select a character for [exhaust] should skip
  // ineligible targets (that are already exhausted)." Apply this to
  // every candidate pool BEFORE pickBy so e.g. strongest-enemy lands
  // on the strongest non-exhausted enemy rather than wasting the
  // effect on a tapped one. `keep-exhausted` is the mirror: the verb
  // only does anything to a character that's already exhausted, so
  // we restrict the candidate pool to already-exhausted targets.
  // Source-as-`self` is intentionally NOT filtered — `self` is an
  // explicit single-target choice, not a selection from a pool, so
  // the verb's own no-op behavior is the right floor there.
  const isEligible = (id: string): boolean => {
    if (verb === 'exhaust') {
      const inst = state.cardInstances.get(id);
      return inst != null && !inst.isExhausted;
    }
    if (verb === 'keep-exhausted') {
      const inst = state.cardInstances.get(id);
      return inst != null && inst.isExhausted;
    }
    if (verb === 'ready') {
      // Skip locked characters — `ready` shouldn't unlock a stasis. The
      // verb's own no-op guard catches `self` / source-fallback paths
      // where this filter is bypassed.
      const inst = state.cardInstances.get(id);
      return inst != null && !(inst.isExhausted && inst.skipNextRefresh);
    }
    if (verb === 'silence') {
      // Match the verb's "nothing meaningful to silence" no-op rules
      // (Verbs.ts): skip already-silenced characters, vanilla
      // characters with no ability slot, and on-play characters whose
      // ability has already fired (on-play can't be silenced
      // pre-trigger). So strongest-enemy lands on the strongest enemy
      // that *has* something to silence, rather than wasting on a
      // vanilla beater.
      const inst = state.cardInstances.get(id);
      if (!inst || inst.abilityDisabled) return false;
      const def = catalog.get(inst.cardId);
      if (!def || def.type !== 'character') return false;
      if (!def.ability) return false;
      if (def.ability.type === 'on-play') return false;
      return true;
    }
    return true;
  };
  const liveIds = (ring: typeof myRing): string[] =>
    ring.map(s => s.instanceId).filter((id): id is string => id !== null);
  const eligible = (ids: string[]): string[] => ids.filter(isEligible);

  // Order both rings' live ids so the side that benefits the active
  // player comes FIRST. `pickBy` uses strict `<` / `>` comparisons,
  // so on a tie the earliest-listed id wins — putting the preferred
  // side first is enough to break the tie in our favor.
  const preferActiveSide = (): string[] => {
    const my = liveIds(myRing);
    const opp = liveIds(oppRing);
    // Negative verb → prefer hitting the opponent (active player wants
    // foes to take the hit). Positive or unknown verb → prefer
    // landing on the active player's own side. Verb-less calls fall
    // back to active-player-side preference; matches the
    // player-favors-self intuition.
    if (verb !== undefined && !isPositiveVerb(verb)) {
      return [...opp, ...my];
    }
    return [...my, ...opp];
  };

  switch (target) {
    case 'self':
      return [sourceId];

    case 'weakest-ally': {
      const others = eligible(liveIds(myRing).filter(id => id !== sourceId));
      if (others.length === 0) {
        // Fall back to source iff source itself is eligible. For
        // verb-less calls this is just "source"; for exhaust it
        // gates on source not already being exhausted, since picking
        // an ineligible self-fallback would waste the effect.
        return isEligible(sourceId) ? [sourceId] : [];
      }
      return [pickBy(others, state, catalog, 'min')];
    }

    case 'strongest-ally': {
      const others = eligible(liveIds(myRing).filter(id => id !== sourceId));
      if (others.length === 0) {
        return isEligible(sourceId) ? [sourceId] : [];
      }
      return [pickBy(others, state, catalog, 'max')];
    }

    case 'all-allies':
      return eligible(liveIds(myRing));

    case 'all-other-allies':
      return eligible(liveIds(myRing).filter(id => id !== sourceId));

    case 'weakest-enemy': {
      const ids = eligible(liveIds(oppRing));
      if (ids.length === 0) return [];
      return [pickBy(ids, state, catalog, 'min')];
    }

    case 'strongest-enemy': {
      const ids = eligible(liveIds(oppRing));
      if (ids.length === 0) return [];
      return [pickBy(ids, state, catalog, 'max')];
    }

    case 'all-enemies':
      return eligible(liveIds(oppRing));

    case 'all-characters':
      return eligible([...liveIds(myRing), ...liveIds(oppRing)]);

    case 'strongest-character': {
      const ordered = eligible(preferActiveSide());
      if (ordered.length === 0) return [];
      return [pickBy(ordered, state, catalog, 'max')];
    }

    case 'weakest-character': {
      const ordered = eligible(preferActiveSide());
      if (ordered.length === 0) return [];
      return [pickBy(ordered, state, catalog, 'min')];
    }

    case 'self-and-weakest-enemy': {
      const ids = eligible(liveIds(oppRing));
      if (ids.length === 0) return [sourceId];
      return [sourceId, pickBy(ids, state, catalog, 'min')];
    }

    case 'self-and-strongest-enemy': {
      const ids = eligible(liveIds(oppRing));
      if (ids.length === 0) return [sourceId];
      return [sourceId, pickBy(ids, state, catalog, 'max')];
    }
  }
}

function pickBy(
  ids: string[],
  state: GameState,
  catalog: Map<string, CardDef>,
  kind: 'min' | 'max',
): string {
  let bestId = ids[0];
  let bestPower = getEffectivePower(state, bestId, catalog);
  for (let i = 1; i < ids.length; i++) {
    const p = getEffectivePower(state, ids[i], catalog);
    if ((kind === 'min' && p < bestPower) || (kind === 'max' && p > bestPower)) {
      bestId = ids[i];
      bestPower = p;
    }
  }
  return bestId;
}
